import {
  getAuditData,
  getSelectedBusinessAuditFilters
} from "selectors/audit";
import { getSelectedBusinessData } from "selectors/businesses";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Async from "utils/Async";
import AuditActions from "actions/AuditActions";
import AuditAmountDeviationFilters from "const/AuditAmountDeviationFilters";
import AuditSubsectionRoutes from "const/AuditSubsectionRoutes";
import Constants from "const/Constants";
import DateTimeFormats from "const/DateTimeFormats";
import Numbers from "utils/Numbers";
import moment from "moment";
import useEnvVars from "hooks/useEnvVars";

const useAudit = (currentSection, currentRoute, skip) => {
  const dispatch = useDispatch();

  const data = useSelector(getAuditData);

  const { auditSettings } = useSelector(getSelectedBusinessData);

  const [envVars] = useEnvVars();

  const {
    minAmount: defaultMinAmount = 0,
    amount: defaultAmount = AuditAmountDeviationFilters.DEFAULT_AMOUNT,
    compareAmountBy: defaultCompareAmountBy = AuditAmountDeviationFilters.GREATER_THAN_OR_EQUAL
  } = useSelector(getSelectedBusinessAuditFilters);

  const availableSections = useMemo(() => JSON.stringify(auditSettings), [auditSettings]);

  const {
    text,
    type,
    month,
    sortBy,
    editItem,
    accountId,
    sortOrder,
    page = "1",
    amount = defaultAmount,
    minAmount = defaultMinAmount,
    compareAmountBy = defaultCompareAmountBy,
    pageSize = `${Constants.TABLE_PAGE_SIZE}`,
    toDate = month ? moment(month).endOf("month").format(DateTimeFormats.API_DATE) : undefined,
    fromDate = month ? moment(month).startOf("month").format(DateTimeFormats.API_DATE) : undefined
  } = envVars;

  const [searchTransactionId] = (editItem || "").split(".");

  const skipFetch = skip
    || !currentSection
    || (currentRoute === AuditSubsectionRoutes.AMOUNT_DEVIATION && !compareAmountBy);

  const fetchData = useCallback(async(clearList = false, backgroundUpdate = false) => {
    if (skipFetch) return null;

    const betweenType = compareAmountBy === AuditAmountDeviationFilters.BETWEEN;

    const parsedAmount = Numbers.parseFloat(amount);

    const parsedMinAmount = Numbers.parseFloat(minAmount);

    const result = await dispatch(AuditActions.fetchAuditList({
      currentSection,
      currentRoute,
      clearList,
      backgroundUpdate,
      filters: {
        text: searchTransactionId || text,
        fromDate,
        toDate,
        accountId,
        type,
        compareAmountBy,
        amount: betweenType ? Math.min(parsedAmount, parsedMinAmount) : parsedAmount,
        maxAmount: betweenType ? Math.max(parsedAmount, parsedMinAmount) : undefined
      },
      sortings: sortBy ? { id: sortBy, desc: sortOrder === "desc" } : null,
      offset: (page - 1) * pageSize,
      limit: +pageSize,
      dependencies: [
        availableSections
      ]
    }));

    return result;
  }, [
    page,
    text,
    type,
    amount,
    sortBy,
    toDate,
    fromDate,
    pageSize,
    accountId,
    minAmount,
    skipFetch,
    sortOrder,
    currentRoute,
    currentSection,
    compareAmountBy,
    availableSections,
    searchTransactionId,
    dispatch
  ]);

  const refetch = useCallback((backgroundUpdate) => {
    fetchData(false, backgroundUpdate);
  }, [fetchData]);

  useLayoutEffect(() => {
    fetchData();
  }, [fetchData]);
  useLayoutEffect(() => {
    return Async.setInterval(
      () => fetchData(false, true), Constants.DATA_LIST_UPDATE_INTERVAL
    );
  }, [fetchData]);

  return { data, refetch };
};

export default useAudit;
